// Firebase App Check token attach for the SMS-signup flow, scoped to new
// (unregistered) phone numbers.
//
// Unlike the AWS WAF step-up flow (stepUpChallenge.ts, currently inert
// pending Infra), this attaches proactively on every request instead of
// reacting to a 428, so there's no retry contract to get wrong. Fail-open
// client-side: a failure to obtain a token sends why instead of the token.
// firebase/app and firebase/app-check are dynamically imported below
// (loadAppCheckSdk(), appCheckHeaderValue()) rather than imported at the
// top level: both login views that pull in this module render for every
// market, and a static import would bundle the Firebase SDK for all of them
// even though only the FR views ever load it. Type-only imports are fine
// here — they're erased at compile time, no bundle cost.
import type { FirebaseApp } from 'firebase/app';
import type { AppCheck } from 'firebase/app-check';
import environment from './environment';
import { logInfo, logWarning } from './remoteLogger';

const LOG_TAG = 'appCheckToken';

declare global {
  interface Window {
    FIREBASE_APPCHECK_DEBUG_TOKEN?: boolean | string;
  }
}

// Dev-only: the reCAPTCHA Enterprise key is domain-restricted to the real
// FR/GB domains, so it can never pass from localhost — App Check's own
// debug-token mechanism is the supported way around that for local testing
// (see https://firebase.google.com/docs/app-check/web/debug-provider).
// Setting this to `true` makes the SDK generate and log a random debug
// token to the console on first local run; register it once in the
// Firebase console (App Check → this web app → manage debug tokens) and
// local requests pass App Check from then on, regardless of domain. Never
// set outside IS_DEV — this must never reach a real build.
const enableDebugTokenIfDev = (): void => {
  if (environment.IS_DEV) {
    window.FIREBASE_APPCHECK_DEBUG_TOKEN = true;
    logInfo(
      LOG_TAG,
      'App Check debug mode enabled — check the console for a debug token' +
        ' to register in the Firebase console (App Check → manage debug' +
        ' tokens) if this is the first run on this machine/browser.'
    );
  }
};

// Not market-gated: only the config presence matters. In practice only the
// markets that present the phone-input view (currently France) ever call
// this, so gating by country here would just be a second, redundant place
// to keep in sync with that — see environment.ts's getFirebaseConfig().
export const isAppCheckEnabled = (): boolean =>
  !!environment.FIREBASE_CONFIG && !!environment.RECAPTCHA_ENTERPRISE_SITE_KEY;

let appCheckPromise: Promise<AppCheck> | undefined;

// Initializes the Firebase app + App Check instance once and memoizes the
// promise — a second call returns the same promise rather than
// re-initializing, same discipline as loadWafSdk() in stepUpChallenge.ts.
export const loadAppCheckSdk = (): Promise<AppCheck> => {
  if (appCheckPromise) {
    return appCheckPromise;
  }

  if (!isAppCheckEnabled()) {
    appCheckPromise = Promise.reject(
      new Error('App Check is not configured for this market')
    );
    // Same retry discipline as the failure path below — both call sites
    // gate on isAppCheckEnabled() before calling this, so today this only
    // matters for a hypothetical direct caller, but a market becoming
    // enabled mid-session (unlikely, but environment.ts is re-evaluated at
    // module load, not per-call) shouldn't get stuck replaying this
    // rejection for the rest of the session either.
    appCheckPromise.catch(() => {
      appCheckPromise = undefined;
    });
    return appCheckPromise;
  }

  appCheckPromise = (async (): Promise<AppCheck> => {
    const { FIREBASE_CONFIG, RECAPTCHA_ENTERPRISE_SITE_KEY } = environment;
    if (!FIREBASE_CONFIG || !RECAPTCHA_ENTERPRISE_SITE_KEY) {
      throw new Error('App Check is not configured for this market');
    }

    enableDebugTokenIfDev();

    const { getApps, initializeApp } = await import('firebase/app');
    const { initializeAppCheck, ReCaptchaEnterpriseProvider } = await import(
      'firebase/app-check'
    );

    // Reuse an existing default app instance rather than always calling
    // initializeApp(): a retry after a first attempt already created the
    // app (e.g. initializeAppCheck() itself failed) would otherwise throw
    // "Firebase App named '[DEFAULT]' already exists" and turn a transient
    // failure into a permanent one.
    const app: FirebaseApp = getApps()[0] ?? initializeApp(FIREBASE_CONFIG);
    return initializeAppCheck(app, {
      provider: new ReCaptchaEnterpriseProvider(RECAPTCHA_ENTERPRISE_SITE_KEY),
      isTokenAutoRefreshEnabled: true,
    });
  })();

  // Clear the memoized promise on failure so a later call retries
  // initialization instead of replaying this same rejection for the rest
  // of the session.
  appCheckPromise.catch(() => {
    appCheckPromise = undefined;
  });

  return appCheckPromise;
};

// Why no App Check token could be attached. Sent after an `absent:` prefix,
// so the strings are a wire contract shared with the native clients. Their
// `not_ready` cannot happen here: the header is awaited before the request.
type AppCheckAbsentReason =
  | 'attestation_rejected'
  | 'provider_unavailable'
  | 'network_error';

// The exchange never answered, so nothing judged the caller. Codes that do
// carry a status are handled below instead.
const NETWORK_ERROR_CODES = new Set([
  'appCheck/fetch-network-error',
  'appCheck/fetch-parse-error',
]);

// reCAPTCHA Enterprise scores rather than refuses, so a refusal arrives as a
// 403 from the token exchange, never as a reCAPTCHA error. 403 is not
// unambiguous — a wrong API key or a deleted project gives the same — and the
// SDK then replays it for a day without re-asking, so one misconfigured key
// reads as a fleet of refusals. Check the config before believing a spike.
const statusReason = (httpStatus: number): AppCheckAbsentReason => {
  if (httpStatus === 403) {
    return 'attestation_rejected';
  }
  // The exchange answered, so nothing was unreachable: a rate limit or a 5xx
  // is transient, and any other status is this client being misconfigured.
  if (httpStatus === 429 || httpStatus >= 500) {
    return 'network_error';
  }
  return 'provider_unavailable';
};

// FirebaseError is read structurally: importing it would pull firebase/app
// into every market's bundle, which the dynamic imports above avoid.
const absentReason = (error: unknown): AppCheckAbsentReason => {
  const { code, customData } = (error ?? {}) as {
    code?: unknown;
    customData?: Record<string, unknown>;
  };

  // The status is the endpoint's verdict; the code says only how it was
  // wrapped, so read the status first.
  const httpStatus = customData?.httpStatus;
  if (typeof httpStatus === 'number') {
    return statusReason(httpStatus);
  }
  if (typeof code === 'string' && NETWORK_ERROR_CODES.has(code)) {
    return 'network_error';
  }
  // Everything else is the provider failing to deliver, not a verdict —
  // including reCAPTCHA failing to run, which a script blocker causes as
  // readily as a bot. That keeps attestation_rejected meaningful.
  return 'provider_unavailable';
};

const absentHeaderValue = (reason: AppCheckAbsentReason): string =>
  `absent:${reason}`;

// Fail-open: every failure in this chain resolves to a reason rather than
// throwing.
const appCheckHeaderValue = async (): Promise<string> => {
  try {
    const appCheck = await loadAppCheckSdk();
    const { getToken } = await import('firebase/app-check');
    const { token } = await getToken(appCheck);
    return token || absentHeaderValue('provider_unavailable');
  } catch (error) {
    const reason = absentReason(error);
    logWarning(
      LOG_TAG,
      `Failed to obtain an App Check token: ${reason}`,
      error instanceof Error ? error : undefined
    );
    return absentHeaderValue(reason);
  }
};

// Headers to spread into a request. The header always goes out, carrying the
// reason when there is no token.
export const appCheckHeaders = async (): Promise<Record<string, string>> => ({
  'X-Firebase-AppCheck': await appCheckHeaderValue(),
});
